import {
  ActivityEvent,
  OtcMainTab,
  OtcRequestItem,
  OtcRequestStatus,
  TimelineEvent,
} from "../../../otc-requests/types";
import { TransactionItem } from "../../types";
import { formatCurrencyAmount, getCurrencySymbol } from "../../utils/formatCurrency";
import { normalizeTransactionStatus } from "../../utils/statusLookup";

/**
 * Sanitizes any raw value to prevent empty arrays '[]', objects '{}',
 * or serialized object artifacts from appearing in the user interface.
 */
export const sanitizeOtcString = (val: unknown, fallback: string = "-"): string => {
  if (val === null || val === undefined) return fallback;
  if (Array.isArray(val)) {
    if (val.length === 0) return fallback;
    const items = val.filter(
      (item) => item !== null && item !== undefined && typeof item !== "object"
    );
    return items.length > 0 ? items.join(", ") : fallback;
  }
  if (typeof val === "object") {
    const extracted =
      (val as any).value ?? (val as any).name ?? (val as any).label;
    if (extracted && typeof extracted !== "object") {
      const trimmed = String(extracted).trim();
      if (trimmed && trimmed !== "{}" && trimmed !== "[]") return trimmed;
    }
    return fallback;
  }
  const str = String(val).trim();
  if (
    str === "" ||
    str === "[]" ||
    str === "{}" ||
    str === "null" ||
    str === "undefined" ||
    str === "[object Object]"
  ) {
    return fallback;
  }
  return str;
};

/**
 * Pure data adapter that transforms a backend TransactionItem
 * (with nested otc and business objects) into the OtcRequestItem
 * format expected by the OTC detail UI components.
 */
export const mapTransactionToOtcRequest = (
  transaction: TransactionItem,
  identifierFallback?: string
): OtcRequestItem => {
  const otcDetails = (transaction.otc || {}) as Record<string, any>;

  // Source & Target currencies directly from transaction / OTC data
  const sourceCurrency = (otcDetails.currency || transaction.currency || "").toUpperCase();
  const targetCurrency = (otcDetails.currencyTo || transaction.currencyTo || "").toUpperCase();

  // Numerical transaction amount
  const transactionAmount = Math.abs(Number(otcDetails.amount || transaction.amount) || 0);

  // Safely extract business name (supports string or { value, status, reason } object)
  const rawBusinessName = transaction.business?.name;
  const resolvedBusinessName =
    typeof rawBusinessName === "object" && rawBusinessName !== null
      ? (rawBusinessName as any).value || (rawBusinessName as any).name
      : typeof rawBusinessName === "string"
        ? rawBusinessName
        : undefined;

  const customerFullName =
    resolvedBusinessName ||
    transaction.customer?.name ||
    transaction.accountName ||
    "Customer";

  const customerEmail =
    transaction.business?.email ||
    transaction.customerEmail ||
    transaction.customer?.email ||
    "-";

  const customerPhone =
    transaction.business?.phone ||
    transaction.customerPhone ||
    transaction.customer?.phone ||
    "-";

  // Wallet balance extraction and formatting
  const walletCurrency = transaction.business?.wallet?.currency || sourceCurrency;
  const rawBalanceValue =
    transaction.balance !== undefined
      ? Number(transaction.balance)
      : transaction.business?.wallet?.balance !== undefined
        ? Number(transaction.business.wallet.balance)
        : undefined;

  const formattedWalletBalance =
    transaction.walletBalance ||
    (rawBalanceValue !== undefined
      ? formatCurrencyAmount(rawBalanceValue, walletCurrency)
      : formatCurrencyAmount(0, walletCurrency));

  // Date and Time formatting
  const rawTimestamp =
    otcDetails.createdAt ||
    transaction.createdAt ||
    transaction.transactionDate ||
    transaction.updatedAt;

  const formattedDate = rawTimestamp
    ? new Date(rawTimestamp).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : transaction.date || "-";

  const formattedDateTime = rawTimestamp
    ? `${formattedDate} • ${new Date(rawTimestamp).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })}`
    : "-";

  // Extract raw rates from OTC subdocument
  const rawCustomerRequestedRate =
    otcDetails.customerRate ??
    otcDetails.requestedRate ??
    otcDetails.rate ??
    otcDetails.exchangeRate ??
    otcDetails.marketRate ??
    transaction.requestedRateValue ??
    transaction.requestedRate ??
    transaction.rate;

  const rawPlatformInitialRate =
    otcDetails.currentRate ??
    otcDetails.ourRate ??
    otcDetails.initialRate ??
    otcDetails.marketRate ??
    transaction.ourRateValue ??
    transaction.ourRate ??
    rawCustomerRequestedRate;

  const rawAdminOfferedRate =
    otcDetails.offeredRate ??
    otcDetails.agreedRate ??
    otcDetails.approvedRate ??
    transaction.offeredRateValue ??
    transaction.offeredRate ??
    rawCustomerRequestedRate;

  const customerRequestedRate =
    Number(rawCustomerRequestedRate) ||
    (transaction.amountTo && transactionAmount
      ? Number(transaction.amountTo) / transactionAmount
      : 0);

  const platformInitialRate = Number(rawPlatformInitialRate) || customerRequestedRate;
  const adminOfferedRate = Number(rawAdminOfferedRate) || customerRequestedRate;

  // Format exchange rates without truncating/abbreviating decimals
  const formatExchangeRate = (rateValue?: number): string => {
    if (!rateValue || isNaN(rateValue) || rateValue <= 0) return "-";
    const symbol = getCurrencySymbol(targetCurrency).trim();
    const rateStr = rateValue.toString();
    const [intPart, decimalPart] = rateStr.split(".");
    const formattedInt = Number(intPart).toLocaleString("en-US");
    const formattedNumber = decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;
    return `${symbol}${formattedNumber} / ${sourceCurrency}`;
  };

  const formattedPlatformInitialRate = formatExchangeRate(platformInitialRate);
  const formattedCustomerRequestedRate = formatExchangeRate(customerRequestedRate);
  const formattedAdminOfferedRate = formatExchangeRate(adminOfferedRate);

  // Prioritize unique OTC request ID strictly from the otc object
  const otcObjectId =
    otcDetails._id ||
    otcDetails.id ||
    otcDetails.otcId ||
    (transaction.otc as any)?._id ||
    (transaction.otc as any)?.id;

  const otcRequestId =
    otcObjectId ||
    transaction.otcRequestId ||
    transaction.requestId ||
    transaction._id ||
    identifierFallback;

  const referenceIdentifier =
    transaction.TransactionReference ||
    transaction.RequestReference ||
    transaction.reference ||
    transaction._id ||
    identifierFallback || '-';

  // Counterparty and bank recipient details
  const recipientFullName = sanitizeOtcString(
    transaction.accountName ||
      transaction.counterparty ||
      transaction.recipientDetails?.name,
    "-"
  );

  const recipientAccountNumber = sanitizeOtcString(
    transaction.accountNumber ||
      transaction.bankAccountNumber ||
      transaction.recipientDetails?.accountNumber,
    "-"
  );

  const recipientBankName = sanitizeOtcString(
    transaction.bankName ||
      transaction.bank ||
      transaction.recipientDetails?.bankName,
    "-"
  );

  const recipientSwiftCode = sanitizeOtcString(
    transaction.swiftCode ||
      transaction.swift ||
      transaction.recipientDetails?.swiftCode,
    ""
  );

  const recipientRoutingNumber = sanitizeOtcString(
    transaction.routingNumber ||
      transaction.sortCode ||
      transaction.recipientDetails?.routingNumber,
    ""
  );

  // Calculate amount recipient is to receive using effective exchange rate
  const effectiveSettlementRate =
    adminOfferedRate > 0
      ? adminOfferedRate
      : customerRequestedRate > 0
        ? customerRequestedRate
        : 0;

  const calculatedRecipientAmount =
    otcDetails.amountTo ??
    transaction.amountTo ??
    (transactionAmount && effectiveSettlementRate
      ? transactionAmount * effectiveSettlementRate
      : undefined);

  const formattedRecipientToReceive =
    transaction.recipientToReceive ||
    (calculatedRecipientAmount !== undefined
      ? formatCurrencyAmount(calculatedRecipientAmount, targetCurrency)
      : "-");

  // Status mapping
  const rawStatus = normalizeTransactionStatus(
    otcDetails.status || transaction.status || ""
  );

  const isCompleted =
    rawStatus === "completed" ||
    rawStatus === "success" ||
    rawStatus === "successful";
  const isApproved =
    rawStatus === "request_approved" || rawStatus === "approved";
  const isOfferSent = rawStatus === "offer_sent";
  const isOfferAccepted = rawStatus === "offer_accepted";
  const isAgreed = rawStatus === "agreed";
  const isAwaitingFunding = rawStatus === "awaiting_funding";
  const isAwaitingUpdate = rawStatus === "awaiting_update";
  const isProcessing = rawStatus === "processing";
  const isRejected = rawStatus === "rejected";
  const isFailed = rawStatus === "failed";
  const isCanceled = rawStatus === "canceled" || rawStatus === "cancelled";
  const isDeclined = rawStatus === "declined";
  const isExpired = rawStatus === "expired";
  const isUnderReview =
    rawStatus === "under_review" ||
    rawStatus === "pending" ||
    rawStatus === "submitted";

  const isTransactionFlagged =
    Boolean(transaction.isFlagged) || rawStatus === "flagged";

  const resolvedState: OtcRequestStatus = isTransactionFlagged
    ? "Flagged"
    : isCompleted
      ? "Completed"
      : isProcessing
        ? "Processing"
      : isOfferAccepted
        ? "Offer accepted"
        : isAgreed
          ? ("Agreed" as OtcRequestStatus)
          : isAwaitingFunding
            ? ("Awaiting funding" as OtcRequestStatus)
            : isAwaitingUpdate
              ? ("Awaiting update" as OtcRequestStatus)
              : isApproved
                ? "Request approved"
              : isOfferSent
                ? "Offer sent"
                : isRejected
                  ? "Rejected"
                  : isFailed
                    ? ("Failed" as OtcRequestStatus)
                    : isDeclined
                      ? "Declined"
                      : isCanceled
                        ? "Canceled"
                        : isExpired
                          ? ("Expired" as OtcRequestStatus)
                          : isUnderReview
                            ? "Under review"
                            : (transaction.state as OtcRequestStatus) || "Under review";

  const resolvedSection: OtcMainTab = isCompleted
    ? "Completed"
    : isProcessing ||
      isApproved ||
      isOfferSent ||
      isOfferAccepted ||
      isAgreed ||
      isAwaitingFunding ||
      isAwaitingUpdate
      ? "Processing"
      : isRejected || isFailed || isCanceled || isDeclined || isExpired
        ? "Closed"
        : "New";

  // Build timeline events from history array
  const historyTimeline: TimelineEvent[] = (otcDetails.history || []).map(
    (historyEntry: any, index: number) => {
      const entryDate = new Date(historyEntry.at);
      const formattedEntryDateTime = isNaN(entryDate.getTime())
        ? historyEntry.at
        : `${entryDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })} • ${entryDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })}`;

      return {
        id: `tl-${index}-${historyEntry.at}`,
        title: historyEntry.message || historyEntry.action,
        timestamp: formattedEntryDateTime,
        completed: true,
      };
    }
  );

  const defaultTimeline: TimelineEvent[] = [
    ...(isCompleted
      ? [
          {
            id: `tl-comp-${Date.now()}`,
            title: "Transaction completed",
            timestamp: formattedDateTime,
            completed: true,
          },
        ]
      : []),
    {
      id: `tl-sub-${Date.now()}`,
      title: "Request submitted",
      timestamp: formattedDateTime,
      completed: true,
    },
  ];

  // Build activity logs from history array
  const historyActivityLogs: ActivityEvent[] = (otcDetails.history || []).map(
    (historyEntry: any, index: number) => {
      const entryDate = new Date(historyEntry.at);
      const formattedEntryDateTime = isNaN(entryDate.getTime())
        ? historyEntry.at
        : `${entryDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })} • ${entryDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })}`;

      const actorName =
        historyEntry.actor === "system"
          ? "System"
          : historyEntry.actor === transaction.business?.user
            ? customerFullName
            : "Admin";

      const actionIcon: ActivityEvent["icon"] =
        historyEntry.action === "processing"
          ? "status"
          : historyEntry.action === "offer_sent"
            ? "offer"
            : historyEntry.action === "request_approved"
              ? "wallet"
              : "request";

      return {
        id: `act-${index}-${historyEntry.at}`,
        timestamp: formattedEntryDateTime,
        user: actorName,
        action: historyEntry.message || historyEntry.action,
        icon: actionIcon,
      };
    }
  );

  // Transfer fee formatting
  const transactionFeeValue =
    transaction.transactionFee ?? transaction.fee ?? otcDetails.fee;

  const formattedTransferFee =
    transactionFeeValue !== undefined && transactionFeeValue !== null
      ? formatCurrencyAmount(transactionFeeValue, sourceCurrency)
      : undefined;

  // Expiration notice
  const expirationTimestamp = otcDetails.expiresAt;
  const formattedExpiringIn = expirationTimestamp
    ? `Valid until ${new Date(expirationTimestamp).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })}`
    : undefined;

  // Settlement method & Notes / reason
  const settlementMethod = sanitizeOtcString(
    otcDetails.settlementMethod ||
      transaction.settlementMethod ||
      transaction.channel ||
      transaction.method,
    "-"
  );

  const iban = sanitizeOtcString(transaction?.iban || otcDetails?.iban, "");
  const paymentProvider = sanitizeOtcString(
    transaction?.paymentProvider || otcDetails?.paymentProvider,
    ""
  );

  const rawNotes =
    otcDetails.note ??
    otcDetails.reason ??
    transaction.narration ??
    transaction.reason ??
    transaction.remark ??
    transaction.notes;
  const notesAndReason = sanitizeOtcString(rawNotes, "N/A");

  const rawCustomerSource =
    otcDetails.customerSource ??
    transaction.customerSource ??
    (transaction as any).customerSource ??
    (transaction as any).data?.customerSource ??
    (transaction as any).data?.otc?.customerSource;
  const customerSource = sanitizeOtcString(rawCustomerSource, "");

  return {
    id: otcObjectId || otcRequestId,
    otcId: otcObjectId,
    date: formattedDateTime,
    isAdminExternalPayout: Boolean(
      transaction.isAdminExternalPayout ??
        otcDetails.isAdminExternalPayout ??
        (transaction as any).data?.isAdminExternalPayout
    ),
    iban,
    paymentProvider,
    amount: transactionAmount,
    currency: sourceCurrency,
    currencyTo: targetCurrency,
    ourRate: formattedPlatformInitialRate,
    ourRateValue: platformInitialRate,
    requestedRate: formattedCustomerRequestedRate,
    requestedRateValue: customerRequestedRate,
    offeredRate: formattedAdminOfferedRate,
    offeredRateValue: adminOfferedRate,
    customerSource,
    pair: `${sourceCurrency} - ${targetCurrency}`,
    customer: {
      name: customerFullName,
      email: customerEmail,
      phone: customerPhone,
      walletBalance: formattedWalletBalance,
      walletCurrency,
      avatarPattern: transaction.customer?.avatarPattern || "oceanic",
    },
    recipientDetails: {
      name: recipientFullName,
      accountNumber: recipientAccountNumber,
      bankName: recipientBankName,
      swiftCode: recipientSwiftCode,
      routingNumber: recipientRoutingNumber,
    },
    transactionId:
      referenceIdentifier.length > 12
        ? `${referenceIdentifier.slice(0, 5)}...${referenceIdentifier.slice(-4)}`
        : referenceIdentifier,
    reference:
      transaction.RequestReference ||
      transaction.TransactionReference ||
      referenceIdentifier,
    state: resolvedState,
    section: resolvedSection,
    recipientToReceive: formattedRecipientToReceive,
    settlementMethod,
    expiringIn: formattedExpiringIn,
    notes: notesAndReason,
    fee: formattedTransferFee,
    transferFee: formattedTransferFee || "$0.00",
    timeline: historyTimeline.length > 0 ? historyTimeline : defaultTimeline,
    activityLog: historyActivityLogs,
    attachments: [],
  };
};

// Re-export alias for backward compatibility
export const buildOtcFallback = mapTransactionToOtcRequest;
